/** The after-action report.
 *
 * A transcript, not a second opinion: casualties come from the battle state and
 * the starting counts the host snapshotted; experience, artifacts and level-ups
 * come from the event stream. Nothing here recomputes a rule (I10).
 */

import type { ContentRegistry, CreatureDefId, HeroId } from "@game/core";
import { artifactIcon, creatureIcon } from "./art.js";
import { BitmapText } from "../theme/bitmapText.js";
import { compactCount, groupDigits } from "./format.js";
import { ART_ICON, PORTRAIT_SMALL } from "./parts.js";
import { usePhoneSheetFit } from "../theme/phone.js";
import { usePolishStyles } from "./polish.js";
import { MaybeIcon, ScreenStyles } from "./primitives.js";
import { outcomeHeadline } from "./battleReport.js";
import type { BattleReport, SideReport } from "./battleReport.js";
import { StyleLayer, useStyleSheet } from "../theme/styleSheet.js";

export interface BattleResultProps {
  readonly content: ContentRegistry;
  readonly report: BattleReport;
  /** Which side the player was on, for the headline and the flag colours. */
  readonly viewerSide?: 0 | 1 | null;
  readonly attackerName?: string;
  readonly defenderName?: string;
  /** Optional: the atlas key for a hero's portrait, since the report carries
   *  instance ids and only the host knows which `HeroDef` each one is. */
  readonly heroPortrait?: ((hero: HeroId) => string | null) | undefined;
  readonly onClose: () => void;
}

/**
 * The line across the top of the report.
 *
 * `outcomeHeadline` answers for a *participant*: with no side it computes
 * `won === false` and prints "Defeat", so a fight the viewer only watched -
 * two computer players, or a report opened without a resolved `viewerSide` -
 * announced a defeat that happened to nobody. Measured on the shipped report:
 * the banner read DEFEAT directly above a panel headed "Attacker - held the
 * field". An observer is told who won instead.
 */
export function resultHeadline(
  report: BattleReport, viewerSide: 0 | 1 | null,
  attackerName: string, defenderName: string,
): string {
  if (viewerSide !== null) return outcomeHeadline(report, viewerSide);
  if (report.kind === "draw" || report.winner === null) {
    return outcomeHeadline(report, null);
  }
  return `${report.winner === 0 ? attackerName : defenderName} Held the Field`;
}

export function BattleResult(props: BattleResultProps): JSX.Element {
  usePolishStyles();
  useResultStyles();
  const { content, report } = props;
  const viewer = props.viewerSide ?? null;
  const won = viewer !== null && report.winner === viewer;
  const attackerName = props.attackerName ?? "Attacker";
  const defenderName = props.defenderName ?? "Defender";

  const fit = usePhoneSheetFit();
  return (
    <div className={`hs-screen hm-aar ${fit.screenClass}`.trim()} ref={fit.screenRef}
      style={fit.style} role="region" aria-label="Battle result">
      <ScreenStyles />

      <div className="hs-phone-sheet" ref={fit.sheetRef}>
      <div className="hs-screen__bar hs-plank">
        <span className="hs-screen__title">After-action Report</span>
        <span className="hs-screen__sub">
          {`${String(report.rounds)} ${report.rounds === 1 ? "round" : "rounds"}`}
        </span>
        <span className="hs-spacer" />
        <button type="button" className="hs-plate hs-plate--sm" onClick={props.onClose}>Continue</button>
      </div>

      {/* Centred, but capped at the body's height so a long casualty list
          scrolls inside the card instead of running off the top of it. */}
      <div className="hs-screen__body" style={{ justifyContent: "center", alignItems: "center", padding: "16px" }}>
        <div className="hs-result hs-scroll" style={{ maxHeight: "100%" }}>
          <div
            className="hs-result__banner"
            style={won ? undefined : { color: "#e2c9a0" }}
          >
            {resultHeadline(report, viewer, attackerName, defenderName)}
          </div>
          <div className="hs-gilt-rule" style={{ width: "min(560px, 80vw)" }} />

          <div className="hs-result__sides">
            <SidePanel
              content={content}
              side={report.attacker}
              name={attackerName}
              victorious={report.winner === 0}
            />
            <SidePanel
              content={content}
              side={report.defender}
              name={defenderName}
              victorious={report.winner === 1}
            />
          </div>

          <div className="hs-card hs-wood" style={{ width: "100%" }}>
            <div className="hs-card__title">Spoils</div>

            {report.experience.length === 0 ? (
              <div className="hs-tiny hs-muted">No experience was earned.</div>
            ) : report.experience.map((e, i) => {
              const level = report.levelUps.find((l) => l.hero === e.hero) ?? null;
              /* Only draw a portrait when there is one. `MaybeIcon` paints the
                 magenta hatch for a null key, which is the right answer for a
                 key the atlas could not resolve and the wrong one for a host
                 that simply did not pass portraits - the report then reads as a
                 broken sprite instead of a line of spoils. */
              const face = props.heroPortrait?.(e.hero) ?? null;
              return (
                <div className="hs-casualty" key={`xp:${e.hero as string}:${String(i)}`}>
                  {face === null ? null : (
                    <MaybeIcon atlasKey={face} size={PORTRAIT_SMALL / 2} title="Hero" />
                  )}
                  <span className="hs-casualty__name">
                    {`${groupDigits(e.amount)} experience`}
                    {level === null ? "" : ` — reached level ${String(level.level)}`}
                  </span>
                </div>
              );
            })}

            {report.artifacts.length === 0 ? null : (
              <>
                <div className="hs-gilt-rule" style={{ margin: "8px 0" }} />
                <div className="hs-grid">
                  {report.artifacts.map((a, i) => {
                    const def = content.artifacts.get(a.artifact);
                    return (
                      <div className="hs-skill" key={`art:${a.artifact as string}:${String(i)}`}
                        title={def?.description ?? ""}>
                        <MaybeIcon
                          atlasKey={def === undefined ? null : artifactIcon(def)}
                          size={ART_ICON}
                          title={def?.name ?? ""}
                        />
                        <span className="hs-skill__text">
                          <span className="hs-skill__name">{def?.name ?? (a.artifact as string)}</span>
                          <span className="hs-skill__level">taken from the field</span>
                        </span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

function SidePanel(props: {
  readonly content: ContentRegistry;
  readonly side: SideReport;
  readonly name: string;
  readonly victorious: boolean;
}): JSX.Element {
  const { side } = props;
  return (
    <div className={`hs-card ${props.victorious ? "hs-wood" : "hs-stone"}`}>
      <div className="hs-card__title">
        {props.name}
        {props.victorious ? " — held the field" : ""}
      </div>

      {side.lines.length === 0 ? (
        <div className="hs-tiny hs-muted">No troops.</div>
      ) : side.lines.map((line) => (
        <div className="hs-casualty" key={line.creature as string}
          title={`${line.name}: ${groupDigits(line.started)} began, ${groupDigits(line.survived)} walked away`}>
          <MaybeIcon
            atlasKey={creatureIconFor(props.content, line.creature)}
            size={40}
            title={line.name}
          />
          <span className="hs-casualty__name">{line.name}</span>
          <span className="hs-casualty__num">
            <BitmapText scale={1}>{compactCount(line.survived)}</BitmapText>
          </span>
          <span className="hs-casualty__num hs-casualty__num--lost">
            {/* The em dash stays in the serif: the face sets numerals and short
                caps labels, and a dash is neither. */}
            {line.lost === 0 ? "\u2014"
              : <BitmapText scale={1}>{`-${compactCount(line.lost)}`}</BitmapText>}
          </span>
        </div>
      ))}

      <div className="hs-gilt-rule" style={{ margin: "8px 0" }} />
      <div className="hs-casualty">
        <span className="hs-casualty__name">Losses</span>
        <span className="hs-casualty__num hs-casualty__num--lost">
          {`${groupDigits(side.lostTotal)} of ${groupDigits(side.startedTotal)}`}
        </span>
      </div>
    </div>
  );
}

function creatureIconFor(content: ContentRegistry, id: CreatureDefId): string | null {
  const def = content.creatures.get(id);
  return def === undefined ? null : creatureIcon(def);
}

// ------------------------------------------------------------------ styles

const AAR_STYLE_ID = "hm-after-action";

/* The screen the report is read on. The shared hs-screen rule paints a flat
   wash, which is the right ground for a settings panel and the wrong one for
   the end of a battle - on the shipped shot it filled everything the report
   card did not. This is the same lit-from-above board the spellbook is opened
   on, so the report sits on a surface rather than on a fill.
   (The wash used to be cold blue-grey as well; the stone ramp is warm now, so
   only the flatness is still an argument.) */
const AAR_CSS = `
.hs-screen.hm-aar {
  background-color: #24170c;
  background-image:
    radial-gradient(90% 70% at 50% 8%, rgba(255,214,150,0.10) 0%, rgba(0,0,0,0) 62%),
    radial-gradient(120% 95% at 50% 0%, rgba(0,0,0,0) 22%, rgba(0,0,0,0.82) 100%),
    repeating-linear-gradient(122deg, rgba(255,220,170,0.03) 0 2px, rgba(0,0,0,0.08) 2px 6px),
    linear-gradient(180deg, #3a2312 0%, #221408 58%, #120a04 100%);
}
.hm-aar .hs-result__banner { font-size: clamp(30px, 3.4vw, 58px); }
/* The tile a taken artifact sits on is the shared \`.hs-skill\` - quarried
   stone, and warm. It was overridden here because that material used to be
   blue slate; it is not any more, so the report and the hero sheet show a
   trophy on the same surface again. */
.hm-aar .hs-skill__name { color: #f6e6b8; }
.hm-aar .hs-skill__level { color: #c9a967; }
`;

function useResultStyles(): void {
  useStyleSheet(AAR_STYLE_ID, AAR_CSS, StyleLayer.screen);
}
